import { Button, Card, Checkbox, Typography } from '@repo/ui';
import useCurrentKitAndTinyAccount from 'hooks/useCurrentKitAndTinyAccount';
import React, { useEffect, useState } from 'react';
import useCreateAgreement from 'services/agreements/useCreateAgreement';
import useImpersonate from 'services/auth/useImpersonate';
import { AgreementKey } from 'types/Agreement';
import styles from '../ConditionsEducationalModal.module.css';

const CHECKBOX_ACKNOWLEDGEMENTS = {
  'non-diagnostic': 'I understand that this is not a diagnosis of conditions.',
  'have-or-develop':
    'I understand that having flagged metrics does not mean I have or will develop the condition in the future.',
  causation:
    'I understand that flagged metrics do not mean causation for a condition.',
} as const;

type Slug = keyof typeof CHECKBOX_ACKNOWLEDGEMENTS;

type FinalSlideProps = {
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  onAgreed: () => void;
};

export default function FinalSlide({ setVisible, onAgreed }: FinalSlideProps) {
  const { impersonateId } = useImpersonate();
  const { currentTinyAccountId, currentKitId } = useCurrentKitAndTinyAccount();

  const [acknowledgedChecks, setAcknowledgedChecks] = useState<
    ReadonlySet<Slug>
  >(new Set());

  const {
    mutateAsync: createAgreement,
    isPending,
    isError,
  } = useCreateAgreement();

  const SLUGS = Object.keys(CHECKBOX_ACKNOWLEDGEMENTS) as Slug[];

  useEffect(() => {
    if (!!impersonateId && acknowledgedChecks.size === 0) {
      setAcknowledgedChecks(new Set(SLUGS));
    }
  }, [impersonateId]);

  function handleCheck(slug: Slug) {
    setAcknowledgedChecks(prev => {
      const next = new Set(prev);
      if (!next.delete(slug)) next.add(slug);
      return next;
    });
  }

  const disabled = !SLUGS.every(slug => acknowledgedChecks.has(slug));

  async function handleProceed() {
    if (!currentTinyAccountId || !currentKitId || disabled) return;

    if (!!impersonateId) return setVisible(false);

    try {
      await createAgreement({
        tinyaccount_id: currentTinyAccountId,
        agreement_key: AgreementKey.NON_DIAGNOSTIC,
        kit_id: currentKitId,
      });
      onAgreed();
    } catch {
      // caught only to keep the rejection from going unhandled; the modal stays
      // open and isError renders the message, since proceeding without a stored
      // agreement is the thing this gate exists to prevent
    }
  }

  return (
    <>
      <Typography variant='heading-l' className={styles.headingText}>
        Some reminders before you explore these connections
      </Typography>
      <div className={styles.checkboxCards}>
        {(Object.entries(CHECKBOX_ACKNOWLEDGEMENTS) as [Slug, string][]).map(
          ([slug, cb]) => (
            <Card key={slug}>
              <div className={styles.checkboxCard}>
                <Checkbox
                  onCheck={() => handleCheck(slug)}
                  checked={acknowledgedChecks.has(slug)}
                />
                {cb}
              </div>
            </Card>
          ),
        )}
      </div>
      <Button
        label='View connections'
        color='purple-300'
        width='full'
        disabled={disabled}
        onClick={handleProceed}
        loading={isPending}
      />
      {isError ? (
        <Typography variant='body-s' color='red-400'>
          We could not save your acknowledgement. Please try again.
        </Typography>
      ) : null}
    </>
  );
}
